import FormSection from '@/components/Forms/FormSection';
import { intercomTarget } from '@/helpers/intercomTarget';
import usePermissions from '@/hooks/usePermissions';
import {
  Accordion,
  Box,
  Button,
  FormikRadioGroup,
  FormikSelectInput,
  FormikTextInput,
  Toggle,
} from '@palmetto/palmetto-components';
import { Field, FieldArray, useFormikContext } from 'formik';
import { ChangeEvent, SyntheticEvent, useCallback, useState } from 'react';

import {
  GeothermalCapacityTonsOptions,
  GeothermalEquipmentTypeLabels,
  GeothermalIndoorUnitType,
  GeothermalIndoorUnitTypeLabels,
  GeothermalLoopType,
  GeothermalLoopTypeLabels,
  GeothermalManufacturer,
  UserPermissions,
} from 'types';

const loopTypeOptions = Object.values(GeothermalLoopType).map((type) => ({
  id: type,
  label: GeothermalLoopTypeLabels[type],
  value: type,
}));

const indoorUnitTypeOptions = Object.values(GeothermalIndoorUnitType).map((type) => ({
  id: type,
  label: GeothermalIndoorUnitTypeLabels[type],
  value: type,
}));

const capacityTonsOptions = GeothermalCapacityTonsOptions.map((tons) => ({
  label: `${tons} tons`,
  value: tons.toString(),
}));

const manufacturerOptions = Object.values(GeothermalManufacturer).map((mfg) => ({ label: mfg, value: mfg }));

interface SystemPanelContentProps {
  index: number;
  isDisabled: boolean;
  isManufacturerDisabled: boolean;
}

const SystemPanelContent = ({ index, isDisabled, isManufacturerDisabled }: SystemPanelContentProps) => {
  const { values, initialValues, setFieldValue } = useFormikContext<any>();
  const prefix = `systems.${index}`;
  const system = values?.systems?.[index];

  // Equipment details are captured at Design Documents, so there is nothing to show before that.
  // Which rows to render follows the persisted values rather than the live ones, so clearing a
  // model mid-edit doesn't pull the field out from under the cursor.
  const equipmentRows = ((initialValues?.systems?.[index]?.equipment ?? []) as any[])
    .map((item, equipmentIndex) => ({ item, equipmentIndex }))
    .filter(({ item }) => Boolean(item?.model) || Boolean(item?.description));

  const handleLoopTypeChange = useCallback(
    (event: SyntheticEvent<HTMLInputElement>) => {
      const selectedValue = (event.target as HTMLInputElement).value;
      if (typeof selectedValue !== 'undefined') {
        setFieldValue(`${prefix}.loopType`, selectedValue);
      }
    },
    [setFieldValue, prefix],
  );

  const handleIndoorUnitTypeChange = useCallback(
    (event: SyntheticEvent<HTMLInputElement>) => {
      const selectedValue = (event.target as HTMLInputElement).value;
      if (typeof selectedValue !== 'undefined') {
        setFieldValue(`${prefix}.indoorUnitType`, selectedValue);
      }
    },
    [setFieldValue, prefix],
  );

  const handleDhwAssistToggle = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFieldValue(`${prefix}.dhwAssist`, event.target.checked);
    },
    [setFieldValue, prefix],
  );

  const handleAuxHeatToggle = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFieldValue(`${prefix}.auxiliaryHeat`, event.target.checked);
      if (!event.target.checked) {
        setFieldValue(`${prefix}.auxiliaryHeatType`, '');
      }
    },
    [setFieldValue, prefix],
  );

  return (
    <Box gap="xl" padding={{ desktop: 'xl 0', base: '0 lg' }}>
      <Box direction={{ base: 'column', desktop: 'row' }} gap="xl">
        <Field
          label="Name"
          name={`${prefix}.name`}
          id={`${prefix}.name`}
          component={FormikTextInput}
          isDisabled={isDisabled}
          width={{ tablet: '4xl' }}
        />
      </Box>

      {/* Loop Type */}
      <Box gap="sm">
        <Box fontSize="sm" fontWeight="medium">
          Loop Type
        </Box>
        <Field
          component={FormikRadioGroup}
          id={`${prefix}.loopType`}
          name={`${prefix}.loopType`}
          options={loopTypeOptions}
          isDisabled={isDisabled}
          onChange={handleLoopTypeChange}
        />
      </Box>

      {/* Indoor Unit Type */}
      <Box gap="sm">
        <Box fontSize="sm" fontWeight="medium">
          Indoor Unit Type
        </Box>
        <Field
          component={FormikRadioGroup}
          id={`${prefix}.indoorUnitType`}
          name={`${prefix}.indoorUnitType`}
          options={indoorUnitTypeOptions}
          isDisabled={isDisabled}
          onChange={handleIndoorUnitTypeChange}
        />
      </Box>

      {/* Capacity Tons */}
      <Box maxWidth="3xl">
        <Field
          label="Capacity (tons)"
          name={`${prefix}.capacityTons`}
          id={`${prefix}.capacityTons`}
          component={FormikSelectInput}
          options={capacityTonsOptions}
          placeholder="Select capacity..."
          isDisabled={isDisabled}
        />
      </Box>

      {/* Manufacturer */}
      <Box maxWidth="3xl">
        <Field
          label="Manufacturer"
          name={`${prefix}.manufacturer`}
          id={`${prefix}.manufacturer`}
          component={FormikSelectInput}
          options={manufacturerOptions}
          placeholder="Select manufacturer..."
          helpText={
            isManufacturerDisabled
              ? 'Changing manufacturer requires a new customer contract. If needed, void current contract and re-quote.'
              : undefined
          }
          isDisabled={isDisabled || isManufacturerDisabled}
        />
      </Box>

      {equipmentRows.length > 0 && (
        <Box gap="lg">
          <Box fontSize="sm" fontWeight="medium">
            Equipment
          </Box>
          {equipmentRows.map(({ item, equipmentIndex }) => (
            <Box key={item.type ?? equipmentIndex} gap="lg">
              {equipmentRows.length > 1 && item.type && (
                <Box fontWeight="medium" color="body-secondary">
                  {GeothermalEquipmentTypeLabels[item.type as keyof typeof GeothermalEquipmentTypeLabels]}
                </Box>
              )}
              <Box direction={{ base: 'column', desktop: 'row' }} gap="lg">
                <Box flex="auto" minWidth="0">
                  <Field
                    label="Model"
                    name={`${prefix}.equipment.${equipmentIndex}.model`}
                    id={`${prefix}.equipment.${equipmentIndex}.model`}
                    component={FormikTextInput}
                    isDisabled={isDisabled}
                  />
                </Box>
                <Box flex="auto" minWidth="0">
                  <Field
                    label="Description (optional)"
                    name={`${prefix}.equipment.${equipmentIndex}.description`}
                    id={`${prefix}.equipment.${equipmentIndex}.description`}
                    component={FormikTextInput}
                    isDisabled={isDisabled}
                  />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* DHW Assist */}
      <Box direction="row" gap="lg" alignItems="center">
        <Box fontWeight="medium" fontSize="sm">
          DHW Assist (Desuperheater)
        </Box>
        <Toggle
          id={`${prefix}.dhwAssist`}
          isChecked={Boolean(system?.dhwAssist)}
          onChange={handleDhwAssistToggle}
          isDisabled={isDisabled}
          label=""
        />
      </Box>

      {/* Auxiliary Heat */}
      <Box gap="sm">
        <Box direction="row" gap="lg" alignItems="center">
          <Box fontWeight="medium" fontSize="sm">
            Auxiliary Heat
          </Box>
          <Toggle
            id={`${prefix}.auxiliaryHeat`}
            isChecked={Boolean(system?.auxiliaryHeat)}
            onChange={handleAuxHeatToggle}
            isDisabled={isDisabled}
            label=""
          />
        </Box>
        {system?.auxiliaryHeat && (
          <Box maxWidth="3xl">
            <Field
              label="Auxiliary Heat Type"
              name={`${prefix}.auxiliaryHeatType`}
              id={`${prefix}.auxiliaryHeatType`}
              component={FormikTextInput}
              placeholder="e.g. Electric Resistance, Gas Furnace"
              isDisabled={isDisabled}
            />
          </Box>
        )}
      </Box>

      {/* Lease Amount */}
      <Box direction={{ base: 'column', desktop: 'row' }} gap="xl">
        <Box flex="auto" minWidth="0" gap="md">
          <Field
            label="Lease Amount"
            name={`${prefix}.leaseAmount`}
            id={`${prefix}.leaseAmount`}
            component={FormikTextInput}
            prefix="$"
            width="2xl"
            isDisabled={isDisabled}
            onFocus={(event: any) => {
              if (event?.target?.value === '0') {
                setFieldValue(`${prefix}.leaseAmount`, '');
              }
            }}
            onBlur={(event: any) => {
              const val = parseFloat(event?.target?.value);
              setFieldValue(`${prefix}.leaseAmount`, isNaN(val) ? 0 : val);
            }}
            onChange={(event: any) => {
              const val = event?.target?.value;
              if (val.endsWith('.')) {
                setFieldValue(`${prefix}.leaseAmount`, val);
              } else {
                const num = Number(val);
                if (!isNaN(num)) {
                  setFieldValue(`${prefix}.leaseAmount`, val);
                }
              }
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

const DEFAULT_SYSTEM = {
  name: '',
  loopType: undefined,
  indoorUnitType: undefined,
  capacityTons: undefined,
  dhwAssist: false,
  auxiliaryHeat: false,
  auxiliaryHeatType: '',
  leaseAmount: 0,
  manufacturer: undefined,
  equipment: [],
};

interface SystemSectionProps {
  isManufacturerDisabled?: boolean;
}

export const SystemSection = ({ isManufacturerDisabled = false }: SystemSectionProps) => {
  const { values } = useFormikContext<any>();
  const permissions = usePermissions();
  const userOnlyHasReaderPermission = permissions?.every((permission) =>
    permission.includes(UserPermissions.reader),
  );

  const [openPanels, setOpenPanels] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    values?.systems?.forEach((_: any, i: number) => {
      initial[String(i + 1)] = true;
    });
    return initial;
  });

  const handleTogglePanel = useCallback((panelIndex: string) => {
    setOpenPanels((prev) => ({ ...prev, [panelIndex]: !prev[panelIndex] }));
  }, []);

  const handleAddSystem = useCallback(
    (push: (obj: any) => void) => {
      const newIndex = values.systems.length;
      const closeExisting = Object.keys(openPanels).reduce(
        (acc, key) => ({ ...acc, [key]: false }),
        {} as Record<string, boolean>,
      );
      setOpenPanels({ ...closeExisting, [String(newIndex + 1)]: true });
      push({ ...DEFAULT_SYSTEM, name: `System ${newIndex + 1}` });
    },
    [values?.systems?.length, openPanels],
  );

  return (
    <Box padding={{ base: '0 2xs' }} data-intercom-target={intercomTarget('system-design', 'system-config')}>
      <FieldArray
        name="systems"
        render={({ push, remove }) => (
          <Accordion>
            <Box>
              {values?.systems?.map((_system: any, index: number) => {
                const panelIndex = String(index + 1);
                return (
                  <Accordion.Panel isOpen={openPanels[panelIndex]} key={`system-${index}`}>
                    <Accordion.PanelSummary
                      hasCaret="right"
                      onToggle={() => handleTogglePanel(panelIndex)}
                      isDetailsOpen={openPanels[panelIndex]}
                      padding={{
                        desktop: !openPanels[panelIndex] ? 'xl 0' : 'xl 0 0 0',
                        base: 'xl lg',
                      }}
                    >
                      <Box direction="row" gap="xs" alignItems="center">
                        <Box fontSize="xl" fontWeight="medium">
                          {values?.systems?.[index]?.name || `System ${index + 1}`}
                        </Box>
                        {values?.systems?.length > 1 && (
                          <Button
                            variant="tertiary"
                            tone="neutral"
                            size="sm"
                            onClick={(e: React.MouseEvent) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const confirmed = window.confirm('Are you sure you want to remove this system?');
                              if (!confirmed) return;
                              remove(index);
                            }}
                            iconPrefix="trash"
                          />
                        )}
                      </Box>
                    </Accordion.PanelSummary>
                    <FormSection baseStyle={{ display: 'block' }}>
                      <SystemPanelContent
                        index={index}
                        isDisabled={userOnlyHasReaderPermission}
                        isManufacturerDisabled={isManufacturerDisabled}
                      />
                    </FormSection>
                  </Accordion.Panel>
                );
              })}
              <Button
                variant="secondary"
                iconPrefix="add"
                style={{ alignSelf: 'flex-end', marginTop: '16px' }}
                onClick={() => handleAddSystem(push)}
                data-intercom-target={intercomTarget('system-design', 'add-system')}
              >
                Add System
              </Button>
            </Box>
          </Accordion>
        )}
      />
    </Box>
  );
};
